// AUPRÉA — Étape 3 du wizard : Préconisations + 3 formules + mode « Montrer au client »
import { useState, useEffect, useMemo } from "react";
import {
  C, Btn, Spinner, Alert,
  parcoursApi, prestationsApi, reglagesApi, fmtEur } from "./shared";
import { computeAnalyse, buildFormules } from "./preconisationsEngine";
import { PARCOURS_CATALOG } from "./parcoursQuestionnaires";
import { ComposerProposition } from "./Composer";
import { EnvoisProposition } from "./EnvoisProposition";
import { ArgumentsVente } from "./ArgumentsVente";


// ── Score card (variantes selon type_parcours) ──────────────────────────────
const ScoreCard = ({ analyse, typeParcours, fiche, presentation }) => {
  const sc = analyse.scores;
  const tp = PARCOURS_CATALOG.find(p => p.code === typeParcours);
  const bigMode = presentation;

  if (typeParcours === "bilan_succession") {
    return (
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: bigMode ? 32 : 20, marginBottom: 20 }} data-testid="score-card">
        <div style={{ fontSize: bigMode ? 15 : 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 700, marginBottom: bigMode ? 12 : 6 }}>Bilan patrimonial · Particulier</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: bigMode ? 24 : 16, flexWrap: "wrap" }}>
          <div style={{ fontSize: bigMode ? 84 : 54, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>{sc.pct}%</div>
          <div style={{ fontSize: bigMode ? 24 : 15, fontWeight: 700, color: sc.libGlobal.color, fontFamily: "'Playfair Display', serif" }}>{sc.libGlobal.label}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: bigMode ? 20 : 10 }}>
          {[
            { key: "p1", label: "Organisation administrative", score: sc.p1, lib: sc.pilierLibs.p1 },
            { key: "p2", label: "Protection juridique & volontés", score: sc.p2, lib: sc.pilierLibs.p2 },
            { key: "p3", label: "Optimisation financière & fiscale", score: sc.p3, lib: sc.pilierLibs.p3 },
          ].map(p => (
            <div key={p.key} style={{ padding: bigMode ? 20 : 12, background: C.gray2, borderRadius: 10, borderTop: `3px solid ${p.lib.color}` }}>
              <div style={{ fontSize: bigMode ? 14 : 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>{p.label}</div>
              <div style={{ fontSize: bigMode ? 34 : 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{p.score}<span style={{ fontSize: bigMode ? 18 : 12, color: C.muted }}>/10</span></div>
              <div style={{ fontSize: bigMode ? 14 : 11, color: p.lib.color, fontWeight: 700, marginTop: 4 }}>{p.lib.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (typeParcours === "bilan_dirigeants") {
    return (
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: bigMode ? 32 : 20, marginBottom: 20 }} data-testid="score-card">
        <div style={{ fontSize: bigMode ? 15 : 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 700, marginBottom: bigMode ? 12 : 6 }}>Bilan Dirigeants · Professionnel</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: bigMode ? 24 : 16, flexWrap: "wrap" }}>
          <div style={{ fontSize: bigMode ? 84 : 54, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>{sc.pct}%</div>
          <div style={{ fontSize: bigMode ? 24 : 15, fontWeight: 700, color: sc.libGlobal.color, fontFamily: "'Playfair Display', serif" }}>{sc.libGlobal.label}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: bigMode ? 12 : 8 }}>
          {[
            { key: "S1", label: "Gouvernance & dépendance" },
            { key: "S2", label: "Structure & valorisation" },
            { key: "S3", label: "Transmission & fiscalité" },
            { key: "S4", label: "Protection famille" },
            { key: "S5", label: "Anticipation & documents" },
          ].map(sect => {
            const s = sc[sect.key]; const lib = sc.sectionLibs[sect.key];
            return (
              <div key={sect.key} style={{ padding: bigMode ? 14 : 10, background: C.gray2, borderRadius: 10, borderTop: `3px solid ${lib.color}` }}>
                <div style={{ fontSize: bigMode ? 11 : 9, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>{sect.label}</div>
                <div style={{ fontSize: bigMode ? 22 : 18, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{s.sum}<span style={{ fontSize: bigMode ? 13 : 11, color: C.muted }}>/{s.max}</span></div>
                <div style={{ fontSize: bigMode ? 11 : 10, color: lib.color, fontWeight: 700, marginTop: 3 }}>{lib.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (typeParcours === "ouvrir_succession") {
    return (
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: bigMode ? 32 : 20, marginBottom: 20 }} data-testid="score-card">
        <div style={{ fontSize: bigMode ? 15 : 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 700, marginBottom: bigMode ? 12 : 6 }}>Diagnostic succession</div>
        <div style={{ fontSize: bigMode ? 34 : 22, fontWeight: 700, color: sc.libGlobal.color, marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>{sc.niveau}</div>
        <div style={{ fontSize: bigMode ? 15 : 12, color: C.mid, marginBottom: 14 }}>Points de complexité : <strong style={{ color: C.navy }}>{sc.complexite}</strong></div>
        {sc.raisons.length > 0 && !bigMode && (
          <ul style={{ fontSize: 12, color: C.mid, paddingLeft: 20, margin: 0 }}>
            {sc.raisons.map((r, i) => <li key={i} style={{ marginBottom: 3 }}>{r}</li>)}
          </ul>
        )}
      </div>
    );
  }

  if (typeParcours === "foncier_clair") {
    return (
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: bigMode ? 32 : 20, marginBottom: 20 }} data-testid="score-card">
        <div style={{ fontSize: bigMode ? 15 : 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 700, marginBottom: bigMode ? 12 : 6 }}>Verdict Foncier Clair</div>
        <div style={{ fontSize: bigMode ? 30 : 20, fontWeight: 700, color: sc.color, fontFamily: "'Playfair Display', serif", lineHeight: 1.3, marginBottom: 12 }}>{sc.verdict}</div>
        {sc.annees > 0 && <div style={{ fontSize: bigMode ? 14 : 12, color: C.mid }}>Occupation continue depuis <strong style={{ color: C.navy }}>{sc.annees} an{sc.annees > 1 ? "s" : ""}</strong></div>}
        {bigMode && <div style={{ marginTop: 20, padding: 14, background: C.amberlt, color: C.amber, borderRadius: 8, fontSize: 14, fontWeight: 600 }}>Verdict indicatif, à confirmer par un notaire.</div>}
      </div>
    );
  }

  return null;
};

// ── Formule card ────────────────────────────────────────────────────────────
const FormuleCard = ({ nom, formule, badge, selected, onSelect, testid, bigMode }) => {
  return (
    <button onClick={onSelect} data-testid={testid}
      style={{
        cursor: "pointer", background: "#fff", border: `2px solid ${selected ? C.gold : C.border}`, borderRadius: 14,
        padding: bigMode ? 24 : 18, textAlign: "left", fontFamily: "inherit",
        display: "flex", flexDirection: "column", gap: 10,
        boxShadow: selected ? "0 8px 24px rgba(200,144,44,0.25)" : "none",
        position: "relative", minHeight: bigMode ? 320 : 260, width: "100%",
      }}>
      {badge && <div style={{ position: "absolute", top: -10, right: 14, background: C.gold, color: "#fff", padding: "3px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: ".07em" }}>{badge}</div>}
      <div style={{ fontSize: bigMode ? 22 : 16, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{nom}</div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        {formule.lignes.length === 0 ? <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>Aucune ligne</div> :
          formule.lignes.map((l, i) => (
            <div key={l.code + i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: bigMode ? 14 : 12, color: l.isRemise ? C.green : C.mid }}>
              <span>{l.isRemise ? "− " : ""}{l.nom}{l.isOrientation && <span style={{ fontSize: 10, color: C.teal, marginLeft: 6, fontWeight: 600 }}>· offert</span>}</span>
              <span style={{ fontWeight: 600, color: l.isRemise ? C.green : C.navy, whiteSpace: "nowrap" }}>{l.isOrientation ? "0 €" : fmtEur(l.prixTTC)}{l.unite ? ` ${l.unite}` : ""}</span>
            </div>
          ))
        }
      </div>
      <div style={{ borderTop: `.5px solid ${C.border}`, paddingTop: 10, marginTop: 6 }}>
        <div style={{ fontSize: bigMode ? 15 : 12, color: C.muted }}>À régler aujourd'hui</div>
        <div style={{ fontSize: bigMode ? 32 : 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{fmtEur(formule.totalOneShot)}</div>
        {formule.totalMonthly > 0 && <div style={{ fontSize: bigMode ? 14 : 11, color: C.mid, marginTop: 3 }}>puis <strong style={{ color: C.navy }}>{fmtEur(formule.totalMonthly)}</strong> / mois</div>}
      </div>
    </button>
  );
};

// ── Mode CLIENT (fullscreen, sans données internes) ─────────────────────────
const ModeClient = ({ parcours, analyse, formules, formuleChoisie, setFormuleChoisie, onQuit, onEnvoyer }) => {
  return (
    <div style={{ position: "fixed", inset: 0, background: C.gray, zIndex: 200, overflowY: "auto", padding: "40px 32px" }} data-testid="mode-client">
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>AUPRÉA · vous accompagne</div>
          <Btn onClick={onQuit} data-testid="mode-client-quit">← Revenir à la vue consultant</Btn>
        </div>
        <div style={{ fontSize: 40, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", marginBottom: 8, lineHeight: 1.1 }}>
          {parcours.fiche?.prenom || "Bonjour"}, votre bilan en clair
        </div>
        <div style={{ fontSize: 18, color: C.mid, marginBottom: 30 }}>Voici ce que nous avons entendu et ce que nous vous proposons pour avancer sereinement.</div>

        <ScoreCard analyse={analyse} typeParcours={parcours.type_parcours} fiche={parcours.fiche} presentation />

        <div style={{ fontSize: 24, fontWeight: 700, color: C.navy, marginTop: 20, marginBottom: 16, fontFamily: "'Playfair Display', serif" }}>Ce que nous vous recommandons</div>
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, marginBottom: 30, border: `.5px solid ${C.border}` }}>
          {analyse.preconisations.filter(p => p.coche && p.code !== "__ALERTE__").map(p => (
            <div key={p.code} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: `.5px dashed ${C.border}`, fontSize: 15 }}>
              <div style={{ fontWeight: 700, color: C.navy, marginBottom: 4 }}>{p.texte.split(":")[0] || p.texte}</div>
              {p.texte.includes(":") && <div style={{ color: C.mid, fontSize: 14 }}>{p.texte.split(":").slice(1).join(":").trim()}</div>}
            </div>
          ))}
        </div>

        <div style={{ fontSize: 24, fontWeight: 700, color: C.navy, marginBottom: 20, fontFamily: "'Playfair Display', serif" }}>3 formules pour vous</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20, marginBottom: 30 }}>
          <FormuleCard nom="Essentiel"    formule={formules.essentiel}   selected={formuleChoisie === "essentiel"}   onSelect={() => setFormuleChoisie("essentiel")}   testid="client-essentiel"   bigMode />
          <FormuleCard nom="Recommandé"   formule={formules.recommande}  badge="RECOMMANDÉ" selected={formuleChoisie === "recommande"} onSelect={() => setFormuleChoisie("recommande")} testid="client-recommande" bigMode />
          <FormuleCard nom="Complet"      formule={formules.complet}     selected={formuleChoisie === "complet"}     onSelect={() => setFormuleChoisie("complet")}     testid="client-complet"     bigMode />
        </div>

        <div style={{ textAlign: "center" }}>
          <Btn variant="gold" onClick={onEnvoyer} data-testid="client-envoyer" style={{ padding: "14px 30px", fontSize: 15 }}>Recevoir ma proposition par e-mail</Btn>
        </div>
      </div>
    </div>
  );
};

// ── Vue Consultant (édition) ────────────────────────────────────────────────
const ConsultantView = ({ parcours, analyse, prestations, reglages, onPatch, onOpenClient, onFinaliser, saveState }) => {
  const [preconisations, setPreconisations] = useState(() => (parcours.preconisations?.length ? parcours.preconisations : analyse.preconisations));
  const [formuleChoisie, setFormuleChoisie] = useState(parcours.formule_choisie || "recommande");
  const [expandPerso, setExpandPerso] = useState(false);

  // recalculer formules à chaque changement de préconisations
  const formules = useMemo(() => buildFormules(preconisations, prestations, reglages), [preconisations, prestations, reglages]);

  // sync back to server when précos/formules/choix changent
  useEffect(() => {
    const t = setTimeout(() => {
      onPatch({
        preconisations,
        formules: { recommande: formules.recommande, essentiel: formules.essentiel, complet: formules.complet },
        formule_choisie: formuleChoisie,
        scores: analyse.scores,
      });
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [preconisations, formuleChoisie]);

  const togglePreco = (i) => setPreconisations(prev => prev.map((p, j) => j === i ? { ...p, coche: !p.coche } : p));

  const findPr = (code) => prestations.find(p => p.code === code);

  return (
    <>
      <ScoreCard analyse={analyse} typeParcours={parcours.type_parcours} fiche={parcours.fiche} />

      {analyse.alertes?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {analyse.alertes.map((a, i) => (
            <Alert key={i} type="warn"><span>⚠</span><span>{a.texte}</span></Alert>
          ))}
        </div>
      )}
      {analyse.pros && (
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>Professionnels concernés</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {analyse.pros.map((p, i) => <span key={i} style={{ background: C.teallt, color: C.teal, padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{p}</span>)}
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, marginBottom: 20 }}>
        <button onClick={() => setExpandPerso(!expandPerso)} data-testid="perso-toggle"
          style={{ width: "100%", padding: "16px 20px", background: "transparent", border: "none", cursor: "pointer",
            display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, fontWeight: 700, color: C.navy, fontFamily: "inherit" }}>
          <span style={{ fontFamily: "'Playfair Display', serif" }}>Personnaliser la formule Recommandée ({preconisations.filter(p => p.coche && p.code !== "__ALERTE__").length} prestations cochées)</span>
          <span style={{ fontSize: 20, color: C.gold }}>{expandPerso ? "−" : "+"}</span>
        </button>
        {expandPerso && (
          <div style={{ padding: "0 20px 20px 20px", borderTop: `.5px solid ${C.border}` }}>
            {preconisations.filter(p => p.code !== "__ALERTE__").map((p, i) => {
              const idx = preconisations.indexOf(p);
              const pr = findPr(p.code);
              return (
                <label key={p.code + idx} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: `.5px dashed ${C.border}`, cursor: "pointer", alignItems: "start" }} data-testid={`perso-${p.code}`}>
                  <input type="checkbox" checked={p.coche} onChange={() => togglePreco(idx)} style={{ marginTop: 4, width: 16, height: 16 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{p.code} · {pr?.nom || "(introuvable)"}</span>
                      {pr && <span style={{ fontSize: 12, color: C.mid, whiteSpace: "nowrap" }}>{pr.prix_affiche}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: C.mid, marginTop: 2 }}>{p.texte}</div>
                    {p.isOption && <span style={{ fontSize: 9, color: C.amber, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 700 }}>Option</span>}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <ArgumentsVente parcours={parcours} preconisations={preconisations} prestations={prestations} />

      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 14, fontFamily: "'Playfair Display', serif" }}>3 formules générées automatiquement</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: 20 }}>
        <FormuleCard nom="Essentiel"    formule={formules.essentiel}   selected={formuleChoisie === "essentiel"}   onSelect={() => setFormuleChoisie("essentiel")}   testid="formule-essentiel" />
        <FormuleCard nom="Recommandé"   formule={formules.recommande}  badge="RECOMMANDÉ" selected={formuleChoisie === "recommande"} onSelect={() => setFormuleChoisie("recommande")} testid="formule-recommande" />
        <FormuleCard nom="Complet"      formule={formules.complet}     selected={formuleChoisie === "complet"}     onSelect={() => setFormuleChoisie("complet")}     testid="formule-complet" />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 24 }}>
        <div style={{ fontSize: 11, color: saveState === "saved" ? C.green : C.mid }}>{saveState === "saving" ? "Enregistrement…" : (saveState === "saved" ? "✓ Enregistré" : "")}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="gold" onClick={onOpenClient} data-testid="btn-montrer-client">👁 Montrer au client</Btn>
          <Btn variant="gold" onClick={onFinaliser} data-testid="btn-finaliser">Finaliser le bilan →</Btn>
        </div>
      </div>
    </>
  );
};

// ── Composant principal Étape 3 ─────────────────────────────────────────────
export const Preconisations = ({ parcours, onFinished, onPatch }) => {
  const [prestations, setPrestations] = useState(null);
  const [reglages, setReglages] = useState(null);
  const [modeClient, setModeClient] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [err, setErr] = useState("");
  const [publicLink, setPublicLink] = useState(parcours?.public_token ? `${window.location.origin}/p/${parcours.public_token}` : "");
  const [publishing, setPublishing] = useState(false);
  const [mailState, setMailState] = useState(parcours.proposition_email ? { email_status: parcours.proposition_email.status, email_to: parcours.proposition_email.to, email_error: parcours.proposition_email.error, at: parcours.proposition_email.at } : null);

  useEffect(() => {
    Promise.all([prestationsApi.list(), reglagesApi.consultant()])
      .then(([p, r]) => { setPrestations(p); setReglages(r); })
      .catch(e => setErr(e?.response?.data?.detail || "Erreur de chargement"));
  }, []);

  const analyse = useMemo(() => computeAnalyse(parcours), [parcours]);

  if (err) return <Alert type="error"><span>⚠</span><span>{err}</span></Alert>;
  if (!prestations || !reglages || !analyse) return <Spinner />;

  const patchServer = async (patch) => {
    setSaveState("saving");
    try {
      const upd = await parcoursApi.patch(parcours.id, patch);
      onPatch && onPatch(upd);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (e) {
      setSaveState("idle"); setErr(e?.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  const finaliser = async () => {
    await patchServer({ statut: "realise" });
    onFinished && onFinished();
  };

  const publier = async () => {
    setPublishing(true);
    try {
      const axios = (await import("axios")).default;
      const tok = localStorage.getItem("auprea_token");
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/parcours/${parcours.id}/publish`,
        {}, { headers: { Authorization: `Bearer ${tok}` } }
      );
      setPublicLink(`${window.location.origin}/p/${r.data.token}`);
      setMailState(r.data);
      return r.data;
    } catch (e) { const m = e?.response?.data?.detail || "Erreur"; setErr(m); return { email_error: m }; }
    finally { setPublishing(false); }
  };

  const renvoyer = async () => {
    setPublishing(true);
    try {
      const axios = (await import("axios")).default;
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/parcours/${parcours.id}/envoyer-proposition`,
        {}, { headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } });
      setMailState(r.data);
      return r.data;
    } catch (e) { const m = e?.response?.data?.detail || "Erreur"; setErr(m); return { email_error: m }; }
    finally { setPublishing(false); }
  };

  const waText = encodeURIComponent(`Bonjour ${parcours.fiche?.prenom || ""}, voici votre proposition personnalisée AUPRÉA : vous pouvez choisir votre formule et régler en ligne en toute sécurité. ${publicLink}`);

  return (
    <>
      <ConsultantView
        parcours={parcours}
        analyse={analyse}
        prestations={prestations}
        reglages={reglages}
        onPatch={patchServer}
        onOpenClient={() => setModeClient(true)}
        onFinaliser={finaliser}
        saveState={saveState}
      />

      <ComposerProposition parcours={parcours}
        formules={parcours.formules && Object.keys(parcours.formules).length > 1 ? parcours.formules : buildFormules(analyse.preconisations, prestations, reglages)}
        onEnvoyee={(res) => { if (res.token) { setPublicLink(`${window.location.origin}/p/${res.token}`); setMailState(res); } }} />

      {publicLink && <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginTop: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 8, fontFamily: "'Playfair Display', serif" }}>Lien de paiement public</div>
        {publicLink ? (
          <>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input readOnly value={publicLink} style={{ flex: 1, minWidth: 300, padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: "monospace", fontSize: 12, background: C.gray2 }} data-testid="public-link" />
              <Btn small onClick={() => { navigator.clipboard.writeText(publicLink); }} data-testid="copy-link">Copier</Btn>
              {!(parcours.proposition?.documents || mailState?.token) && <Btn small onClick={renvoyer} disabled={publishing} data-testid="resend-proposition-email">✉ Renvoyer par e-mail</Btn>}
              <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
                style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "#25D366", padding: "7px 14px", borderRadius: 8, textDecoration: "none" }}>WhatsApp</a>
              {!(parcours.proposition?.documents || mailState?.token) && <Btn small variant="gold" onClick={() => { if (window.confirm("Régénérer le lien ? L'ancien lien ne fonctionnera plus et un nouvel e-mail sera envoyé au client.")) publier(); }} disabled={publishing} data-testid="regenerate-link">Régénérer</Btn>}
            </div>
            <MailStatus st={mailState} />
            {!parcours.proposition?.numero && <EnvoisProposition parcoursId={parcours.id} refreshKey={mailState?.at || mailState?.email_status} />}
            <div style={{ fontSize: 11, color: C.mid, marginTop: 8 }}>Le client peut choisir sa formule, payer (en une fois ; 2 fois uniquement sur accord de l'admin) et prendre rendez-vous depuis ce lien.</div>
          </>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: C.mid, marginBottom: 10 }}>Générez un lien sécurisé : la proposition est envoyée automatiquement par e-mail au client{parcours.fiche?.email ? ` (${parcours.fiche.email})` : ""}.</div>
            <Btn variant="gold" onClick={publier} disabled={publishing} data-testid="publish-btn">{publishing ? "Envoi…" : "🔗 Générer le lien et envoyer au client"}</Btn>
          </div>
        )}
      </div>}

      {modeClient && (
        <ModeClient
          parcours={parcours}
          analyse={analyse}
          formules={parcours.formules && Object.keys(parcours.formules).length ? parcours.formules : buildFormules(analyse.preconisations, prestations, reglages)}
          formuleChoisie={parcours.formule_choisie || "recommande"}
          setFormuleChoisie={(f) => patchServer({ formule_choisie: f })}
          onQuit={() => setModeClient(false)}
          onEnvoyer={async () => {
            const r = publicLink ? await renvoyer() : await publier();
            alert(r?.email_status === "sent" ? `Proposition envoyée à ${r.email_to} ✓` : `E-mail non envoyé : ${r?.email_error || "erreur"}`);
          }}
        />
      )}
    </>
  );
};

const MailStatus = ({ st }) => {
  if (!st) return null;
  const ok = st.email_status === "sent";
  const msg = ok ? `Proposition envoyée par e-mail à ${st.email_to}${st.at ? ` le ${new Date(st.at).toLocaleString("fr-FR")}` : ""}`
    : st.email_status === "no_email" ? "Aucun e-mail dans la fiche client : partagez le lien via WhatsApp ou copiez-le."
    : `E-mail non envoyé${st.email_error ? ` : ${st.email_error}` : ""}`;
  return <div data-testid="proposition-email-status" style={{ fontSize: 12, marginTop: 10, fontWeight: 600, color: ok ? C.green : C.amber }}>{ok ? "✓ " : "⚠ "}{msg}</div>;
};
